/**
 * Store -> franchise lookup for the recipeV2 demo handlers (recipeV2Handlers.ts).
 *
 * NOTE: recipeV2 has no real backend yet (see /memories/session/plan.md), so this
 * file stays MSW-backed for now. Items/ItemAndComponent themselves DO have a real
 * backend - their own handler (itemAndComponentHandlers.ts) has been removed and
 * this file was trimmed down to just the bit recipeV2Handlers.ts / recipeV2Data.ts
 * still need.
 */

const storeFranchiseLookup: Record<string, number> = {
  '001': 1, '002': 1, '003': 1, '004': 1,
  '100': 2, '101': 2,
  'VAP001': 10, 'VAP002': 10, 'VAP003': 10, 'VAP100': 10,
  'FG001': 11, 'FG002': 11, 'FG003': 11, 'FG100': 11,
};

const normalizeStoreNumber = (storeNumber?: string | null): string =>
  (storeNumber || '').trim().toUpperCase();

export function getFranchiseIdForStore(storeNumber?: string | null): number | null {
  const normalizedStoreNumber = normalizeStoreNumber(storeNumber);
  if (!normalizedStoreNumber) return null;

  return storeFranchiseLookup[normalizedStoreNumber] ?? null;
}

/**
 * Base recipe catalogue, still used by recipeV2Data.ts (recipeV2 has no real
 * backend yet) to derive its V2 recipe list.
 */
export interface MockRecipeComponent {
  itemNumber: string;
  description: string;
  quantity: number;
  uom: string;
}

export interface MockRecipe {
  id: number;
  franchiseId: number;
  itemNumber: string;
  description: string;
  components: MockRecipeComponent[];
}

export interface MockRecipeComponentDto {
  quantity?: number;
  weight?: number;
  uom?: string;
  componentId?: number;
  componentItemNumber?: string;
  componentDescription?: string;
  componentCostPriceOnHand?: number;
}

const normalizeLegacyItemNumber = (itemNumber: string): string => {
  const trimmed = itemNumber.trim().toUpperCase();
  if (trimmed.includes('-')) return trimmed;

  const match = trimmed.match(/^([A-Z]+)(\d{3})$/);
  if (!match) return trimmed;
  return `${match[1]}-${match[2]}`;
};

const mapStaticComponentToDto = (
  component: MockRecipeComponent,
  index: number,
): MockRecipeComponentDto => {
  const normalizedItemNumber = normalizeLegacyItemNumber(component.itemNumber);
  return {
    componentId: index + 1,
    componentItemNumber: normalizedItemNumber,
    componentDescription: component.description,
    quantity: component.quantity,
    weight: component.quantity,
    uom: component.uom,
    componentCostPriceOnHand: undefined,
  };
};

export function getRecipeComponentsForApi(recipe: MockRecipe): MockRecipeComponentDto[] {
  return recipe.components.map(mapStaticComponentToDto);
}

// Demo dataset trimmed to just the three related recipes (pizza + its two sub-recipes).
export const mockRecipes: MockRecipe[] = [
  {
    id: 102,
    franchiseId: 10,
    itemNumber: 'VAP-MARGHERITA-SALE',
    description: 'Pizza Margherita',
    components: [
      { itemNumber: 'VAP-FLOUR-001', description: '00 Flour (Caputo)', quantity: 0.25, uom: 'kg' },
      { itemNumber: 'VAP-MOZZ-001', description: 'Fior di Latte Mozzarella', quantity: 0.125, uom: 'kg' },
      { itemNumber: 'VAP-TOMATO-001', description: 'San Marzano Tomatoes', quantity: 0.1, uom: 'kg' },
      { itemNumber: 'VAP-BASIL-001', description: 'Fresh Basil', quantity: 3, uom: 'Leaves' },
      { itemNumber: 'VAP-OLIVE-001', description: 'Extra Virgin Olive Oil', quantity: 0.015, uom: 'Litre' },
    ],
  },
  {
    id: 301,
    franchiseId: 10,
    itemNumber: 'VAP-BREAD-DOUGH',
    description: 'Artisan Bread Dough',
    components: [
      { itemNumber: 'VAP-FLOUR-001', description: '00 Flour (Caputo)', quantity: 1.0, uom: 'kg' },
      { itemNumber: 'VAP-YEAST-001', description: 'Dried Active Yeast', quantity: 0.01, uom: 'kg' },
      { itemNumber: 'VAP-SALT-001', description: 'Sea Salt', quantity: 0.02, uom: 'kg' },
      { itemNumber: 'VAP-BUTTER-001', description: 'Unsalted Butter', quantity: 0.05, uom: 'kg' },
      { itemNumber: 'VAP-WATER-001', description: 'Water', quantity: 0.6, uom: 'Litre' },
    ],
  },
  {
    id: 302,
    franchiseId: 10,
    itemNumber: 'VAP-TOMATO-SAUCE',
    description: 'House Tomato Sauce',
    components: [
      { itemNumber: 'VAP-TOMATO-001', description: 'San Marzano Tomatoes', quantity: 1.0, uom: 'kg' },
      { itemNumber: 'VAP-GARLIC-001', description: 'Fresh Garlic', quantity: 0.02, uom: 'kg' },
      { itemNumber: 'VAP-ONION-001', description: 'White Onion', quantity: 0.15, uom: 'kg' },
      { itemNumber: 'VAP-BASIL-001', description: 'Fresh Basil', quantity: 0.01, uom: 'kg' },
      { itemNumber: 'VAP-OLIVE-001', description: 'Extra Virgin Olive Oil', quantity: 0.03, uom: 'Litre' },
    ],
  },
];
